import React from 'react';
import useGameStore from '../store/gameStore';
import {
  EC_PALIERS, EC_ACQUISITION, EC_LOGISTIQUE, EC_PREPARATION,
  getPalier, defaultEcommerce,
} from '../utils/magasin';
import { computeAmelFx } from '../utils/ameliorations';
import { formatMoney } from '../utils/format';
import { Globe, MousePointerClick, PackageCheck, Undo2, Store } from 'lucide-react';
import { toast } from '../components/ui/sonner';

export default function Ecommerce() {
  const retail = useGameStore(s => s.retail);
  const treasury = useGameStore(s => s.treasury);
  const totalDays = useGameStore(s => s.totalDays);
  const lancerPalierEcommerce = useGameStore(s => s.lancerPalierEcommerce);
  const toggleAcquisitionEc = useGameStore(s => s.toggleAcquisitionEc);
  const setLogistiqueEc = useGameStore(s => s.setLogistiqueEc);

  if (!retail) return <div className="text-slate-400 text-sm" data-testid="ecommerce-empty">Aucun magasin actif.</div>;
  const ec = retail.ecommerce || defaultEcommerce();
  const palier = getPalier(ec.palier);
  const suivant = getPalier(ec.palier + 1);
  const st = ec.stats || {};
  const magFx = { partCcPct: computeAmelFx(retail.magasin).partCcPct };
  const emps = (retail.equipe?.employes || []).filter(e => !e.finJour || e.finJour > totalDays);
  const nbPrep = emps.filter(e => e.metierId === 'preparateur_commandes').length;
  const capacite = EC_PREPARATION.capacite_base_jour + nbPrep * EC_PREPARATION.capacite_par_preparateur;
  const vendEnLigne = ec.palier >= 2;

  return (
    <div className="space-y-5" data-testid="ecommerce-page">
      <div>
        <p className="overline">{palier ? palier.nom : 'Pas encore de présence en ligne'} · capacité {capacite} commandes/j</p>
        <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight text-foreground mt-0.5 flex items-center gap-2">
          <Globe className="w-6 h-6 text-accent" /> E-commerce
        </h1>
      </div>

      {/* Funnel du jour */}
      {vendEnLigne && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="funnel-web">
          <div className="surface-card p-3"><p className="text-[10px] uppercase font-bold text-slate-500">Visites hier</p><p className="text-xl font-black font-display text-foreground">{st.visites || 0}</p></div>
          <div className="surface-card p-3"><p className="text-[10px] uppercase font-bold text-slate-500">Commandes</p><p className="text-xl font-black font-display text-foreground">{st.commandes || 0}{st.commandesPerdues > 0 && <span className="text-xs text-red-400 ml-1">(−{st.commandesPerdues} perdues)</span>}</p></div>
          <div className="surface-card p-3"><p className="text-[10px] uppercase font-bold text-slate-500">CA web (net retours)</p><p className="text-xl font-black font-display text-money">{formatMoney(st.caWeb || 0)}</p></div>
          <div className="surface-card p-3"><p className="text-[10px] uppercase font-bold text-slate-500">Retours hier</p><p className="text-xl font-black font-display text-danger">{formatMoney(st.retoursEuros || 0)}</p></div>
          <div className="surface-card p-3"><p className="text-[10px] uppercase font-bold text-slate-500">CA web cumulé</p><p className="text-xl font-black font-display text-foreground">{formatMoney(st.caWebCumul || 0)}</p></div>
        </div>
      )}
      {vendEnLigne && st.commandesPerdues > 0 && (
        <p className="text-xs text-amber-400" data-testid="alerte-capacite">⚠ {st.commandesPerdues} commande(s) perdue(s) hier faute de capacité de préparation — recrutez un préparateur (page Équipe).</p>
      )}

      {/* Paliers */}
      <div className="surface-card p-4" data-testid="paliers-ec">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-3">Votre présence en ligne</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {EC_PALIERS.map(p => {
            const atteint = ec.palier >= p.niveau;
            const estSuivant = suivant?.niveau === p.niveau;
            return (
              <div key={p.id} className={`rounded-lg border p-3 flex flex-col ${atteint ? 'border-accent bg-accent/10' : 'border-ink-600 bg-ink-900/40'}`} data-testid={`palier-${p.id}`}>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-slate-100">{p.niveau}. {p.nom}</span>
                  {atteint && <span className="ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-emerald-600/20 text-emerald-400">Actif</span>}
                </div>
                <p className="text-[11px] text-slate-500 mt-1 leading-snug flex-1">{p.description}</p>
                <p className="text-[11px] font-mono text-slate-400 mt-1.5">{formatMoney(p.cout_lancement)} + {formatMoney(p.cout_mensuel)}/mois</p>
                {estSuivant && (
                  <button
                    type="button"
                    onClick={() => {
                      const res = lancerPalierEcommerce();
                      if (res?.ok) toast(`${res.palier.nom} lancé !`, { description: `${formatMoney(res.palier.cout_lancement)} débités.` });
                      else if (res?.reason === 'fonds') toast.error('Trésorerie insuffisante');
                    }}
                    disabled={treasury < p.cout_lancement}
                    className="mt-2 bg-accent text-white px-3 py-1.5 rounded text-xs font-bold hover:bg-accent-hover transition-colors disabled:opacity-40"
                    data-testid={`lancer-${p.id}`}
                  >
                    Lancer ({formatMoney(p.cout_lancement)})
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Acquisition */}
      {vendEnLigne && (
        <div className="surface-card p-4" data-testid="acquisition-ec">
          <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-3 flex items-center gap-2"><MousePointerClick className="w-4 h-4 text-accent" /> Acquisition de trafic</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {EC_ACQUISITION.map(a => {
              const actif = !!ec.acquisition?.[a.id];
              const verrouille = (a.niveau_min || 0) > ec.palier;
              return (
                <button
                  key={a.id}
                  type="button"
                  disabled={verrouille}
                  onClick={() => {
                    const res = toggleAcquisitionEc(a.id);
                    if (res?.ok) toast(res.actif ? `${a.nom} activé` : `${a.nom} coupé`);
                  }}
                  className={`text-left rounded-lg border p-3 transition-all disabled:opacity-40 ${actif ? 'border-accent bg-accent/10 ring-1 ring-accent' : 'border-ink-600 bg-ink-900/40 hover:border-ink-500'}`}
                  data-testid={`acquisition-${a.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span>{a.emoji}</span>
                    <span className="font-bold text-sm text-slate-100">{a.nom}</span>
                    <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded ${actif ? 'bg-emerald-600/20 text-emerald-400' : 'bg-ink-700 text-slate-500'}`}>{verrouille ? 'Hub requis' : actif ? 'Actif' : 'Inactif'}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-snug">{a.description}</p>
                  <p className="text-[11px] font-mono text-slate-300 mt-1">{a.cout_mensuel > 0 ? `${formatMoney(a.cout_mensuel)}/mois` : 'Sans abonnement'}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Logistique & retours + omnicanal */}
      {vendEnLigne && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="surface-card p-4" data-testid="logistique-ec">
            <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-3 flex items-center gap-2"><PackageCheck className="w-4 h-4 text-accent" /> Logistique & retours</h3>
            <div className="space-y-2">
              {EC_LOGISTIQUE.map(l => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => { setLogistiqueEc(l.id); toast(`Expédition : ${l.nom}`); }}
                  className={`w-full text-left rounded-lg border p-3 transition-all ${ec.logistique === l.id ? 'border-accent bg-accent/10 ring-1 ring-accent' : 'border-ink-600 bg-ink-900/40 hover:border-ink-500'}`}
                  data-testid={`logistique-${l.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span>{l.emoji}</span>
                    <span className="font-bold text-sm text-slate-100">{l.nom}</span>
                    <span className="ml-auto text-[11px] font-mono text-slate-300">{formatMoney(l.cout_par_commande)}/commande</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-2">
                    {l.description} <Undo2 className="w-3 h-3 text-red-400 ml-auto" /> <span className="text-red-400 font-mono">{l.taux_retour_pct} % retours</span>
                  </p>
                </button>
              ))}
            </div>
          </div>
          <div className="surface-card p-4" data-testid="omnicanal-ec">
            <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-3 flex items-center gap-2"><Store className="w-4 h-4 text-accent" /> Omnicanal — Click & collect</h3>
            {magFx.partCcPct > 0 ? (
              <div data-testid="cc-actif">
                <p className="text-2xl font-black font-display text-emerald-400">{magFx.partCcPct} % des commandes retirées en magasin</p>
                <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                  Ces commandes ne coûtent <b>rien à expédier</b> et réduisent les retours de 2 points.
                  Améliorez la zone Click & collect (page Magasin) pour augmenter cette part.
                </p>
              </div>
            ) : (
              <div data-testid="cc-inactif">
                <p className="text-sm font-bold text-amber-400">Pas encore de point de retrait.</p>
                <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                  Aménagez la zone <b>Click & collect</b> de votre magasin (nécessite le back-office) : une partie
                  des commandes web sera retirée sur place — zéro frais d'expédition, moins de retours, plus de passage en boutique.
                </p>
              </div>
            )}
            <p className="text-[11px] text-slate-500 mt-3 pt-3 border-t border-ink-700">
              Équipe web : {nbPrep} préparateur(s) ({capacite} commandes/j) · {emps.some(e => e.metierId === 'responsable_ecommerce') ? 'Responsable e-commerce en poste (+20 % conversion, +10 % visites)' : 'Pas de responsable e-commerce — recrutez-en un (page Équipe) pour booster conversion et visites'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
